import { useMemo } from 'react';
import { Stack, Tabs, Text } from '@adc/parallax-component-library';
import { LocalDate } from '@js-joda/core';
import { createFileRoute, useNavigate } from '@tanstack/react-router';

import { usePatientSearch } from '~/api/query/patientSearch';
import type { PatientsFilters, PatientSort } from '~/api/types';
import { PatientTable } from '~/components/table/patient/PatientTable';
import type { PatientTableColumnKey } from '~/components/table/patient/types';
import { USER_ROLES } from '~/features/auth/roles';
import {
  PATIENTS_TABS,
  PatientsTabsConfig,
  type PatientsTabsValue,
} from '~/features/patients/constants';
import type { PatientSearchParams } from '~/types/PatientRoute';
import { buildTestId } from '~/utils/buildTestId';
import { protectedRoute } from '~/utils/routesAuthorization';

const TEST_ID = 'patients';
const AVAILABLE_COLUMNS: PatientTableColumnKey[] = [
  'firstName',
  'lastName',
  'dateOfBirth',
  'alertsLast24hours',
  'lactate',
  'status',
];

export const Route = createFileRoute('/outpatient/patients')({
  component: RouteComponent,
  validateSearch: (
    search: Record<string, unknown>,
  ): PatientSearchParams<PatientsTabsValue, PatientSort> => {
    // Validate and parse the search params into a typed state
    const defaultSortDir = search.sortDir === 'asc' ? 'asc' : 'desc';
    const sortDir = search.sortDir ? defaultSortDir : undefined;
    const tab =
      search.tab != undefined &&
      Object.values(PATIENTS_TABS).includes(search.tab as PatientsTabsValue)
        ? (search.tab as PatientsTabsValue)
        : PATIENTS_TABS.all;

    return {
      page: search.page ? Number(search.page) || 1 : undefined,
      sort: (search.sort as PatientTableColumnKey | undefined) ?? undefined,
      search: search.search ? decodeURI(search.search as string) : undefined,
      sortDir,
      tab,
    };
  },
  beforeLoad: () => {
    protectedRoute({
      roles: [USER_ROLES.HcpAdmin, USER_ROLES.HcpUser],
    });
  },
});

function RouteComponent() {
  const {
    page = 1,
    search = '',
    sort = '',
    sortDir = 'desc',
    tab = PATIENTS_TABS.all,
  } = Route.useSearch();

  const navigate = useNavigate({ from: Route.fullPath });

  const filters = useMemo(
    (): PatientsFilters => ({
      createdAfter:
        tab === PATIENTS_TABS.all
          ? undefined
          : LocalDate.now().minusDays(7).toString(),
    }),
    [tab],
  );

  const { data } = usePatientSearch({
    page,
    search: search === '' ? undefined : search,
    sort: sort === '' ? undefined : sort,
    sortDir,
    filters,
  });

  const handleSearch = async (searchValue: string) => {
    // Update the search term and reset to the first page
    await navigate({
      search: (prev) => ({ ...prev, page: 1, search: encodeURI(searchValue) }),
    });
  };

  const handlePageChange = async (newPage: number) => {
    // Update the page number in the search params
    await navigate({
      search: (prev) => ({ ...prev, page: newPage }),
    });
  };

  const handleSort = async (columnKey: PatientTableColumnKey) => {
    // Update the sort column and direction in the search params
    await navigate({
      search: (prev) => ({
        ...prev,
        sort: columnKey,
        sortDir: sort === columnKey && sortDir === 'asc' ? 'desc' : 'asc',
      }),
    });
  };

  return (
    <Stack>
      <Text
        type="$h3.medium"
        marginBottom="$2"
        testID={buildTestId({ testID: TEST_ID, suffix: 'title' })}
      >
        Department: XY
      </Text>

      <Stack marginVertical="$2" flexDirection="row" alignItems="flex-end">
        <Stack flex={1}>
          <Tabs
            tabs={PatientsTabsConfig}
            selectedIndex={tab}
            onSelect={(index) =>
              void navigate({
                search: () => ({ tab: index as PatientsTabsValue }),
              })
            }
            testID={buildTestId({ testID: TEST_ID, suffix: 'tabs' })}
          />
        </Stack>
        <Text
          testID={buildTestId({ testID: TEST_ID, suffix: ['last', 'sync'] })}
          flex={1}
          textAlign="right"
          type="$labelBase.medium"
          paddingRight="$2"
        >
          Last sync:{' '}
          {data?.lastSync
            ? new Date(data.lastSync).toUTCString()
            : 'unavailable'}
        </Text>
      </Stack>
      <PatientTable
        testID={buildTestId({ testID: TEST_ID, suffix: 'table' })}
        page={page}
        search={search}
        sort={sort}
        sortDir={sortDir}
        filters={filters}
        onPageChange={handlePageChange}
        onSearch={handleSearch}
        onSortChange={handleSort}
        availableColumns={AVAILABLE_COLUMNS}
      />
    </Stack>
  );
}
